/**
 * Maps the backend's app-wide {@code BookingErrorCode} (the stable contract in
 * `BookingErrorCode.java`) to an i18n key under `booking.errors.*`. The backend
 * owns the code + a human English message; the frontend localizes by code and
 * falls back to the backend message for any code it doesn't recognize yet.
 *
 * Kept in the shared lib so the marketplace and the per-tenant client-booking
 * SPA localize identically. The catalogs in each app define these keys.
 */

/** Shape of the backend error body ({@code BookingExceptionMapper}). */
export interface BookingErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown> | null;
  retryable?: boolean;
}

/**
 * code → i18n key. Any code NOT listed here (or a missing code) resolves to
 * `null`, signalling the caller to fall back to the backend message. Retryable
 * slot-race codes and hard-stop eligibility codes both map here; whether to
 * show a retry affordance is driven by `retryable` on the body, not the key.
 */
const CODE_TO_KEY: Record<string, string> = {
  TIME_SLOT_NO_LONGER_AVAILABLE: 'booking.errors.timeSlotUnavailable',
  EMPLOYEE_UNAVAILABLE: 'booking.errors.employeeUnavailable',
  LOCATION_UNAVAILABLE: 'booking.errors.locationUnavailable',
  BOOKING_RULE_VIOLATION: 'booking.errors.bookingRuleViolation',
  SERVICE_NOT_BOOKABLE: 'booking.errors.serviceNotBookable',
  ONLINE_BOOKING_DISABLED: 'booking.errors.onlineDisabled',
  MOBILE_BOOKING_DISABLED: 'booking.errors.mobileDisabled',
  CUSTOMER_ADDRESS_REQUIRED: 'booking.errors.addressRequired',
  CUSTOMER_TIMEZONE_REQUIRED: 'booking.errors.timezoneRequired',
  PAYMENT_REQUIRED: 'booking.errors.paymentRequired',
  PAYMENT_FAILED: 'booking.errors.paymentFailed',
  PAYMENT_REFUNDED: 'booking.errors.paymentRefunded',
  PAYMENT_CANCELLED: 'booking.errors.paymentCancelled',
  STRIPE_ACCOUNT_NOT_READY: 'booking.errors.stripeAccountNotReady',
  FEATURE_REQUIRES_UPGRADE: 'booking.errors.featureRequiresUpgrade',
  PLAN_LIMIT_REACHED: 'booking.errors.planLimitReached',
  INVALID_REQUEST: 'booking.errors.generic',
  INTERNAL_ERROR: 'booking.errors.generic',
};

/** The i18n key for a backend error code, or null if the code is unknown. */
export function bookingErrorKey(code: string | undefined | null): string | null {
  if (!code) return null;
  return CODE_TO_KEY[code] ?? null;
}

/**
 * Parses a thrown booking error into `{code, message, details, retryable}`.
 * The shared fetch wrapper (`use-booking-intent.ts`) throws an Error whose
 * `.message` is the raw response body text; for the structured contract that
 * body is JSON. Non-JSON bodies (network errors, plain-text 500s) degrade to
 * `{ message: <raw text> }` so the caller always has something to show.
 */
export function parseBookingError(err: unknown): BookingErrorBody {
  const raw =
    err && typeof err === 'object' && 'message' in err
      ? String((err as { message?: unknown }).message ?? '')
      : String(err ?? '');
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as BookingErrorBody;
    }
  } catch {
    // Not JSON — treat the raw text as the message.
  }
  return { message: raw || undefined };
}

/**
 * A translate function compatible with i18next's `t`. We call it as
 * `t(key, { defaultValue })` — the overload i18next actually exposes — so this
 * type is written to accept the app's `t` from `useTranslation()` directly.
 * The return is coerced to string (i18next's return type is broad).
 */
export type TranslateFn = (
  key: string,
  options?: { defaultValue?: string },
) => unknown;

/**
 * Resolves the user-facing message for a booking error using a translate
 * function `t`. Prefers the localized string for a known code; otherwise falls
 * back to the backend message, then a generic key.
 */
export function localizeBookingError(
  body: BookingErrorBody,
  t: TranslateFn,
): string {
  const key = bookingErrorKey(body.code);
  if (key) {
    // Missing translation → i18next returns the defaultValue, so we fall back
    // to the backend message when we have one (else the key itself).
    return String(t(key, { defaultValue: body.message ?? key }));
  }
  if (body.message) return body.message;
  return String(
    t('booking.errors.generic', {
      defaultValue: 'Something went wrong. Please try again.',
    }),
  );
}
